// src/hooks/useInvestments.js
import { useState, useEffect } from 'react';
import investmentService from '../services/investment.service';
import authService from '../services/auth.service';

export const useInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvestments = async () => {
    const userId = authService.getUserId();
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await investmentService.getUserInvestments(userId);
      // Transform backend data to match frontend format
      const transformedData = data.map(inv => ({
        id: inv.investmentId,
        type: inv.investmentType,
        name: inv.investmentName,
        amount: inv.currentAmount,
        date: inv.startDate
      }));
      setInvestments(transformedData);
    } catch (err) {
      console.error('Error fetching investments:', err);
      setError(err.response?.data?.message || 'Failed to fetch investments');
    } finally {
      setLoading(false);
    }
  };

  const addInvestment = async (investmentData) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const response = await investmentService.createInvestment(userId, investmentData);
      await fetchInvestments(); // Refresh the list
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to add investment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteInvestment = async (investmentId) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      await investmentService.deleteInvestment(investmentId, userId);
      await fetchInvestments(); // Refresh the list
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete investment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getTotalBalance = () => {
    return investments.reduce((sum, inv) => sum + inv.amount, 0);
  };

  const getBalanceByType = () => {
    const typeBalances = {};
    investments.forEach(inv => {
      if (!typeBalances[inv.type]) {
        typeBalances[inv.type] = 0;
      }
      typeBalances[inv.type] += inv.amount;
    });
    return typeBalances;
  };

  useEffect(() => {
    fetchInvestments();
  }, []);

  return {
    investments,
    loading,
    error,
    fetchInvestments,
    addInvestment,
    deleteInvestment,
    getTotalBalance,
    getBalanceByType
  };
};